// Sales Dashboard — revenue/performance overview, wired to the Sales-specific
// backend aggregation queries (getSalesSummary, getSalesByEmployee, etc.) via
// ONE combined GraphQL round trip (getSalesDashboardDataAction), following the
// same pattern as the Reports > Dashboard screen (DashboardScreen.tsx).
//
// Gross/Net Profit, Outstanding Receivables, and Sales vs Target's numerator
// side have no backing data in the current schema (no cost-price field, no
// due/credit concept) — Gross/Net Profit and Outstanding Receivables are
// omitted rather than fabricated. Sales vs Target uses a fixed monthly target
// constant until a real target-setting feature exists.
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { ColumnDef } from "@tanstack/react-table";
import {
  CreditCard,
  IndianRupee,
  Receipt,
  Percent,
  RotateCcw,
  CalendarDays,
  Store,
  Wallet,
} from "lucide-react";
import AppConstant from "../../../Globals/AppConstants";
import { getSalesDashboardDataAction } from "../../../Store/actions/apparelAction";
import ApparelMenu from "../../SubComponents/apparelMenu";
import { StatCard } from "../../../shared/stat-card";
import { DataTable } from "../../../shared/data-table";
import { Skeleton } from "ui/skeleton";
import { ChartCard } from "./ChartCard";
import { TrendAreaChart } from "./TrendAreaChart";
import { CategoryDonutChart } from "./CategoryDonutChart";
import { SalesShareDonutChart } from "./SalesShareDonutChart";
import { CategoryBarChart } from "./CategoryBarChart";
import { GaugeChart } from "./GaugeChart";
import { GroupedBarChart } from "./GroupedBarChart";

// No target-setting feature exists yet — placeholder monthly target until one does.
const MONTHLY_SALES_TARGET = 500000;
const RECENT_SALES_PAGE_SIZE = 10;
const VENDOR_COMPARISON_PAGE_SIZE = 10;

const inr = (v: any) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(v) || 0);
const num = (v: any) => new Intl.NumberFormat("en-IN").format(Number(v) || 0);
// Share of a metric's grand total, e.g. "42.5%". "-" when there is no total to divide by.
const pct = (part: number, total: number) => (total ? `${((part / total) * 100).toFixed(1)}%` : "-");
// Profit as a % of purchase cost, e.g. "25.47%". "-" when there's no purchase to divide by.
const profitPct = (profit: number, purchase: number) => (purchase ? `${((profit / purchase) * 100).toFixed(2)}%` : "-");

const getFinancialYearRange = () => {
  const today = new Date();
  const startYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  return {
    fromDate: moment({ year: startYear, month: 3, day: 1 }).format("YYYY-MM-DD"),
    toDate: moment({ year: startYear + 1, month: 2, day: 31 }).format("YYYY-MM-DD"),
    label: `Apr ${startYear} – Mar ${startYear + 1}`,
  };
};

const SalesDashboardScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
  const { getSalesDashboardData, getSalesDashboardDataLoad } = apparelReducerState;

  const fy = React.useMemo(() => getFinancialYearRange(), []);
  const [recentSalesPage, setRecentSalesPage] = React.useState(1);
  const [vendorComparisonPage, setVendorComparisonPage] = React.useState(1);
  const [activeTab, setActiveTab] = React.useState<"recentSales" | "vendorComparison">("recentSales");

  const fetchDashboard = React.useCallback(
    (page?: number) => {
      const p = page || 1;
      dispatch(
        getSalesDashboardDataAction({
          fromDate: fy.fromDate,
          toDate: fy.toDate,
          offset: (p - 1) * RECENT_SALES_PAGE_SIZE,
        })
      );
    },
    [dispatch, fy]
  );

  React.useEffect(() => {
    fetchDashboard(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRecentSalesPageChange = (nextPage: number) => {
    setRecentSalesPage(nextPage);
    fetchDashboard(nextPage);
  };

  const summary = getSalesDashboardData?.getSalesSummary;
  const loading = getSalesDashboardDataLoad && !summary;

  const salesTrend = (getSalesDashboardData?.getSalesTrend || []).map((r: any) => ({ date: r.date, value: Number(r.amount) || 0 }));
  // Month-wise bars: sum the daily trend into the 12 months (Apr..Mar) of the current FY; months with no sales stay 0.
  const monthlySales = React.useMemo(() => {
    const months = Array.from({ length: 12 }, (_, i) => moment(fy.fromDate).add(i, "months"));
    const totals: Record<string, number> = {};
    (getSalesDashboardData?.getSalesTrend || []).forEach((r: any) => {
      const k = moment(r.date).format("YYYY-MM");
      totals[k] = (totals[k] || 0) + (Number(r.amount) || 0);
    });
    return months.map((m) => ({ label: m.format("MMM YY"), value: totals[m.format("YYYY-MM")] || 0 }));
  }, [getSalesDashboardData?.getSalesTrend, fy]);
  const hasMonthlySales = monthlySales.some((m) => m.value > 0);
  const fyRaw = getSalesDashboardData?.getSalesByFinancialYear;
  const yearlySales = (Array.isArray(fyRaw) ? fyRaw : []).filter((r: any) => r.fyStart != null).map((r: any) => ({
    label: `FY ${r.fyStart}-${String(Number(r.fyStart) + 1).slice(-2)}`,
    value: Number(r.amount) || 0,
  }));
  // Current vs previous calendar month across the last 4 years (backend computes the same months from its clock).
  const curMonthName = moment().format("MMM");
  const prevMonthName = moment().subtract(1, "month").format("MMM");
  const mcRaw = getSalesDashboardData?.getSalesMonthComparison;
  const monthComparison = (Array.isArray(mcRaw) ? mcRaw : []).map((r: any) => ({
    label: String(r.year),
    current: Number(r.currentMonthAmount) || 0,
    previous: Number(r.previousMonthAmount) || 0,
  }));
  const hasMonthComparison = monthComparison.some((r) => r.current > 0 || r.previous > 0);
  const salesByCategory = (getSalesDashboardData?.getSalesByCategory || []).map((r: any) => ({ category: r.category, amount: Number(r.amount) || 0 }));
  const subCategoryRaw = getSalesDashboardData?.getSalesBySubCategory;
  const salesBySubCategory = (Array.isArray(subCategoryRaw) ? subCategoryRaw : []).map((r: any) => ({ category: r.category, amount: Number(r.amount) || 0 }));
  const paymentMethodSplit = (getSalesDashboardData?.getSalesByPaymentMethod || []).map((r: any) => ({ category: r.paymentMethod ?? "Unknown", amount: Number(r.amount) || 0 }));
  const salesByEmployee = (getSalesDashboardData?.getSalesByEmployee || []).map((r: any) => ({ label: r.employeeName ?? "Unknown", value: Number(r.amount) || 0 }));
  const topProducts = (getSalesDashboardData?.getTopProducts || []).map((r: any) => ({ label: r.productName, value: Number(r.revenue) || 0 }));
  const recentSales = getSalesDashboardData?.getSalesRecentBills?.recentBills || [];
  const recentSalesPageInfo = getSalesDashboardData?.getSalesRecentBills?.page;

  // Vendor Sales Comparison — per vendor: purchase (vendor bills), sales of that
  // vendor's products, and profit (sales - purchase). The % under each figure is
  // that vendor's share of the metric's total across all vendors.
  const vendorRows = React.useMemo(
    () =>
      (getSalesDashboardData?.getPurchaseVsSalesByVendor || []).map((r: any) => {
        const purchase = Number(r.purchaseAmount) || 0;
        const sales = Number(r.salesAmount) || 0;
        return { vendorId: r.vendorId, vendorName: r.vendorName, purchase, sales, profit: sales - purchase };
      }),
    [getSalesDashboardData]
  );
  const vendorTotals = React.useMemo(
    () =>
      vendorRows.reduce(
        (t: any, r: any) => ({ purchase: t.purchase + r.purchase, sales: t.sales + r.sales, profit: t.profit + r.profit }),
        { purchase: 0, sales: 0, profit: 0 }
      ),
    [vendorRows]
  );
  const pagedVendorRows = vendorRows.slice(
    (vendorComparisonPage - 1) * VENDOR_COMPARISON_PAGE_SIZE,
    vendorComparisonPage * VENDOR_COMPARISON_PAGE_SIZE
  );

  const recentSalesCsvRows = React.useMemo(
    () => recentSales.map((r: any) => ({ ...r, purchaseDateLabel: r.purchaseDate ? moment(r.purchaseDate).format("DD-MMM-YYYY") : "", totalamount: Number(r.totalamount) || 0 })),
    [recentSales]
  );

  // A new fetch (date range change) can shrink the list below the selected page.
  React.useEffect(() => {
    setVendorComparisonPage(1);
  }, [getSalesDashboardData?.getPurchaseVsSalesByVendor]);

  const vendorComparisonColumns = React.useMemo<ColumnDef<any, any>[]>(() => {
    const metricCell = (value: number, total: number, colored = false) => (
      <div className="tabular-nums leading-tight">
        <div className={colored ? (value >= 0 ? "text-emerald-600" : "text-red-600") : undefined}>{inr(value)}</div>
        {/* <div className="text-xs text-muted-foreground">{pct(value, total)}</div> */}
      </div>
    );
    const profitCell = (profit: number, purchase: number) => (
      <div className="tabular-nums leading-tight">
        <div className={profit >= 0 ? "text-emerald-600" : "text-red-600"}>{inr(profit)}</div>
        <div className={`text-xs ${profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{profitPct(profit, purchase)}</div>
      </div>
    );
    return [
      { id: "vendorName", header: "Vendor", cell: ({ row }) => row.original?.vendorName },
      { id: "purchase", header: "Purchase", cell: ({ row }) => metricCell(row.original.purchase, vendorTotals.purchase) },
      { id: "sales", header: "Sales", cell: ({ row }) => metricCell(row.original.sales, vendorTotals.sales) },
      { id: "profit", header: "Profit", cell: ({ row }) => profitCell(row.original.profit, row.original.purchase) },
    ];
  }, [vendorTotals]);

  const recentSalesColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "invoiceId", header: "Invoice ID", cell: ({ row }) => row.original?.invoiceId ? (
        <button type="button" onClick={() => navigate("/billing", { state: { auditRecordId: row.original.billingId, auditSearchCode: row.original.invoiceId } })} className="whitespace-nowrap text-primary hover:underline">
          {row.original.invoiceId}
        </button>
      ) : "-" },
      { id: "purchaseDate", header: "Purchased Date", cell: ({ row }) => <span className="whitespace-nowrap">{row.original?.purchaseDate ? moment(row.original.purchaseDate).format("DD-MMM-YYYY") : "-"}</span> },
      { id: "customerName", header: "Customer Name", cell: ({ row }) => row.original?.customerName || "-" },
      { id: "mobileNumber", header: "Mobile Number", cell: ({ row }) => row.original?.mobileNumberList || "-" },
      { id: "itemsCount", header: "Items", cell: ({ row }) => <span className="tabular-nums">{num(row.original?.itemsCount)}</span> },
      { id: "totalamount", header: "Total Amount", cell: ({ row }) => <span className="whitespace-nowrap tabular-nums">₹ {Number(row.original?.totalamount || 0).toFixed(2)}</span> },
      { id: "paymentMethodName", header: "Payment Type", cell: ({ row }) => row.original?.paymentMethodName || "-" },
    ],
    [navigate]
  );

  return (
    <div className="mz h-full overflow-y-auto px-6 py-4">
      <ApparelMenu selectedKey="4" />

      <div className="mb-4 flex items-center gap-1 border-b">
        <button type="button" className="border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground">
          {AppConstant.menus.salesDashboard}
        </button>
      </div>

      <div className="mb-4">
        <p className="text-sm text-muted-foreground">Showing current financial year ({fy.label}).</p>
      </div>

      {/* KPI cards */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {loading ? (
          Array.from({ length: 22 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)
        ) : (
          <>
            <StatCard label="Today's Sales" value={inr(summary?.todaysSales)} icon={IndianRupee} />
            <StatCard label="Yesterday's Sales" value={inr(summary?.yesterdaysSales)} icon={IndianRupee} />
            <StatCard label="Weekly Sales" value={inr(summary?.weeklySales)} icon={IndianRupee} />
            <StatCard label="Monthly Sales" value={inr(summary?.monthlySales)} icon={IndianRupee} />
            <StatCard label="Yearly Sales" value={inr(summary?.yearlySales)} icon={IndianRupee} />
            <StatCard label="Total Orders" value={num(summary?.totalOrders)} icon={Receipt} />
            <StatCard label="Average Order Value" value={inr(summary?.averageOrderValue)} icon={CalendarDays} />
            <StatCard label="Refund Amount" value={inr(summary?.refundAmount)} icon={RotateCcw} />
            <StatCard label="Discount Given" value={inr(summary?.discountGiven)} icon={Percent} />
            <StatCard label="Active Suppliers" value={num(summary?.activeSuppliers)} icon={Store} />
            <StatCard label="Today's Return" value={inr(summary?.todaysReturn)} icon={RotateCcw} />
            <StatCard label="Weekly Return" value={inr(summary?.weeklyReturn)} icon={RotateCcw} />
            <StatCard label="Monthly Return" value={inr(summary?.monthlyReturn)} icon={RotateCcw} />
            <StatCard label="Yearly Return" value={inr(summary?.yearlyReturn)} icon={RotateCcw} />
            <StatCard label="Today Credit Given" value={inr(summary?.todaysCreditGiven)} icon={CreditCard} />
            <StatCard label="Today Credit Money Recd." value={inr(summary?.todaysCreditReceived)} icon={Wallet} />
            <StatCard label="Weekly Credit Given" value={inr(summary?.weeklyCreditGiven)} icon={CreditCard} />
            <StatCard label="Weekly Credit Money Recd." value={inr(summary?.weeklyCreditReceived)} icon={Wallet} />
            <StatCard label="Monthly Credit Given" value={inr(summary?.monthlyCreditGiven)} icon={CreditCard} />
            <StatCard label="Monthly Credit Money Recd." value={inr(summary?.monthlyCreditReceived)} icon={Wallet} />
            <StatCard label="Yearly Credit Given" value={inr(summary?.yearlyCreditGiven)} icon={CreditCard} />
            <StatCard label="Yearly Credit Money Recd." value={inr(summary?.yearlyCreditReceived)} icon={Wallet} />
          </>
        )}
      </div>

      {/* Charts */}
      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Revenue Trend" loading={getSalesDashboardDataLoad && !getSalesDashboardData} isEmpty={!salesTrend.length}>
          <TrendAreaChart data={salesTrend} valueFormatter={inr} />
        </ChartCard>
        <ChartCard title={`Month-wise Sales (${fy.label})`} loading={getSalesDashboardDataLoad && !getSalesDashboardData} isEmpty={!hasMonthlySales}>
          <CategoryBarChart data={monthlySales} valueFormatter={inr} />
        </ChartCard>
        <ChartCard title="Year-wise Sales" loading={getSalesDashboardDataLoad && !getSalesDashboardData} isEmpty={!yearlySales.length}>
          <CategoryBarChart data={yearlySales} valueFormatter={inr} />
        </ChartCard>
        <ChartCard title={`${curMonthName} vs ${prevMonthName} — Year-wise`} loading={getSalesDashboardDataLoad && !getSalesDashboardData} isEmpty={!hasMonthComparison}>
          <GroupedBarChart
            data={monthComparison}
            series={[
              { key: "current", label: curMonthName, color: "hsl(var(--primary))" },
              { key: "previous", label: prevMonthName, color: "hsl(var(--muted-foreground))" },
            ]}
            valueFormatter={inr}
          />
        </ChartCard>
        <ChartCard title="Sales by Category" bodyClassName="h-auto sm:h-64" loading={getSalesDashboardDataLoad && !getSalesDashboardData} isEmpty={!salesByCategory.length}>
          <SalesShareDonutChart data={salesByCategory} valueFormatter={inr} />
        </ChartCard>
        <ChartCard title="Sales by Sub-Category" bodyClassName="h-auto sm:h-64" loading={getSalesDashboardDataLoad && !getSalesDashboardData} isEmpty={!salesBySubCategory.length}>
          <SalesShareDonutChart data={salesBySubCategory} valueFormatter={inr} />
        </ChartCard>
        <ChartCard title="Payment Method Distribution" loading={getSalesDashboardDataLoad && !getSalesDashboardData} isEmpty={!paymentMethodSplit.length}>
          <CategoryDonutChart data={paymentMethodSplit} valueFormatter={inr} />
        </ChartCard>
        <ChartCard title="Sales by Employee" loading={getSalesDashboardDataLoad && !getSalesDashboardData} isEmpty={!salesByEmployee.length}>
          <CategoryBarChart data={salesByEmployee} valueFormatter={inr} />
        </ChartCard>
        <ChartCard title="Top Selling Products" loading={getSalesDashboardDataLoad && !getSalesDashboardData} isEmpty={!topProducts.length}>
          <CategoryBarChart data={topProducts} valueFormatter={inr} layout="vertical" />
        </ChartCard>
        <ChartCard title="Sales vs Target" loading={getSalesDashboardDataLoad && !getSalesDashboardData} isEmpty={false}>
          <GaugeChart value={Number(summary?.monthlySales) || 0} target={MONTHLY_SALES_TARGET} valueFormatter={inr} />
        </ChartCard>
      </div>

      {/* Recent Sales / Vendor Sales Comparison — tab switch */}
      <div className="mb-2 flex flex-wrap gap-1 border-b">
        {[
          { key: "recentSales", label: "Recent Sales" },
          { key: "vendorComparison", label: "Vendor Sales Comparison" },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key as "recentSales" | "vendorComparison")}
            className={
              activeTab === tab.key
                ? "border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground"
                : "border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "recentSales" ? (
        <DataTable
          columns={recentSalesColumns}
          data={recentSales}
          loading={getSalesDashboardDataLoad && !getSalesDashboardData}
          csvExport={{ fileName: `recent-sales_${moment().format("YYYY-MM-DD")}`, rows: recentSalesCsvRows, columns: [{ key: "invoiceId", header: "Invoice ID" }, { key: "purchaseDateLabel", header: "Purchased Date" }, { key: "customerName", header: "Customer Name" }, { key: "mobileNumberList", header: "Mobile Number" }, { key: "itemsCount", header: "Items" }, { key: "totalamount", header: "Total Amount" }, { key: "paymentMethodName", header: "Payment Type" }] }}
          pagination={{
            page: recentSalesPageInfo?.currentPage || recentSalesPage,
            total: recentSalesPageInfo?.totalCount || 0,
            pageSize: RECENT_SALES_PAGE_SIZE,
            onChange: onRecentSalesPageChange,
          }}
        />
      ) : (
        <>
          {/* <p className="mb-2 text-xs text-muted-foreground">
            {rangeValue?.from && rangeValue?.to ? "Selected date range" : "All time"} · % = share of total
          </p> */}
          <DataTable
            columns={vendorComparisonColumns}
            data={pagedVendorRows}
            loading={getSalesDashboardDataLoad && !getSalesDashboardData}
            csvExport={{ fileName: `vendor-sales-comparison_${moment().format("YYYY-MM-DD")}`, rows: vendorRows, columns: [{ key: "vendorName", header: "Vendor" }, { key: "purchase", header: "Purchase" }, { key: "sales", header: "Sales" }, { key: "profit", header: "Profit" }] }}
            pagination={{
              page: vendorComparisonPage,
              total: vendorRows.length,
              pageSize: VENDOR_COMPARISON_PAGE_SIZE,
              onChange: setVendorComparisonPage,
            }}
          />
          {/* {vendorRows.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-md border bg-muted/40 px-3 py-2 text-xs">
              <span className="font-medium text-foreground">Total</span>
              <span className="tabular-nums text-muted-foreground">Purchase {inr(vendorTotals.purchase)}</span>
              <span className="tabular-nums text-muted-foreground">Sales {inr(vendorTotals.sales)}</span>
              <span className={`tabular-nums ${vendorTotals.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>Profit {inr(vendorTotals.profit)}</span>
            </div>
          )} */}
        </>
      )}
    </div>
  );
};

export default SalesDashboardScreen;
